/**
 * The list of account cards for account capture: one card per account shell, partner-shared accounts
 * in their own read-only "Shared by …" section, and the existing-LTC-policy card that opens the
 * policy editor. Reports whether every account is complete (and, when the host requires it, that at
 * least one self-funding account is complete).
 *
 * Everything flow-specific comes from the host: selected types, funding sources (live or a guided
 * snapshot), read-only state, a completed session's allocations, and how partner shares are recorded.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from '@client/lib';

import { AddPolicyModal } from '@client/components/AddPolicyModal';
import { getShellExpandKey, useAccountAccordion } from '../hooks/use-account-accordion';
import {
  usePartnerLockedFundingSources,
  type LegacyPartnerSelections,
} from '../hooks/use-partner-locked-funding-sources';
import {
  usePartnerAllocation,
  DEFAULT_CUSTOM_CLIENT_PERCENT,
  type PersistedSelection,
} from '../hooks/use-partner-allocation';
import { countCompleteSelfFundingAccounts } from '../lib/account-completeness';
import { AccountCard, type AllocationCallbacks } from './account-card';
import { ExistingLtcPolicyCard } from './existing-ltc-policy-card';
import type { AssetTypeId } from '../config/funding-source-types';

export interface AccountAccordionListProps {
  className?: string;
  selectedTypes: readonly AssetTypeId[];
  fundingSources: MultipleFundingSources | undefined;
  readOnly: boolean;
  requireSelfFundingAccount?: boolean;
  /** See `FundingSourceAccountsProps.beforeAccountSave`. */
  beforeAccountSave?: () => Promise<boolean>;
  /** A completed guided session's allocations, shown as "(−amount)" on each account. */
  completedAllocations?: Record<string, number>;
  savedAllocationSelections?: Record<string, PersistedSelection>;
  persistAllocationSelections?: (selections: Record<string, PersistedSelection>) => Promise<unknown>;
  legacy?: LegacyPartnerSelections;
  onAllCompleteChange?: (allComplete: boolean) => void;
}

export function AccountAccordionList({
  className,
  selectedTypes,
  fundingSources,
  readOnly,
  requireSelfFundingAccount = false,
  beforeAccountSave,
  completedAllocations,
  savedAllocationSelections,
  persistAllocationSelections,
  legacy,
  onAllCompleteChange,
}: AccountAccordionListProps) {
  const {
    partnerSharedFundingSourceIds,
    partnerFirstName,
    isLoading: isLoadingPartnerData,
  } = usePartnerLockedFundingSources(legacy);
  const { accountShells, allComplete, expandedShellKey, handleToggle, handleSaveComplete } =
    useAccountAccordion({ selectedTypes, fundingSources, partnerSharedFundingSourceIds });
  const { hasPartner, client, partnerClient, getCardAllocationState, applyCardAllocation } =
    usePartnerAllocation({ savedAllocationSelections, persistAllocationSelections, legacy });
  const [existingLtcDrawerOpen, setExistingLtcDrawerOpen] = useState(false);
  const hasRequiredAccount =
    !requireSelfFundingAccount || countCompleteSelfFundingAccounts(fundingSources?.selfFunding) > 0;

  const existingLtcShell = useMemo(
    () => accountShells.find(s => s.isExistingLtcPolicy),
    [accountShells],
  );
  const policyFundingSourceIdForDrawer = existingLtcShell?.policyFundingSourceId ?? undefined;

  const allocationsBySourceId = completedAllocations ?? {};

  const clientName = client?.clientFirstName ?? 'You';
  const partnerName = partnerClient?.clientFirstName ?? 'Partner';

  const buildAllocationCallbacks = useCallback(
    (
      fundingSourceId: string | null,
      assetTypeId: AssetTypeId,
      selfFundingSource?: SelfFundingSource,
    ): AllocationCallbacks | undefined => {
      if (!hasPartner) return undefined;
      return {
        hasPartner: true,
        clientName,
        partnerName,
        initialState: fundingSourceId
          ? getCardAllocationState(fundingSourceId, selfFundingSource)
          : { enabled: false, option: 'mirror' as const, customClientPercent: DEFAULT_CUSTOM_CLIENT_PERCENT },
        onApply: (state, overrides) =>
          applyCardAllocation(
            overrides?.fundingSourceId ?? fundingSourceId ?? '',
            assetTypeId,
            overrides?.sourceData ?? selfFundingSource!,
            state,
          ),
      };
    },
    [hasPartner, clientName, partnerName, getCardAllocationState, applyCardAllocation],
  );

  useEffect(() => {
    onAllCompleteChange?.(allComplete && hasRequiredAccount);
  }, [allComplete, hasRequiredAccount, onAllCompleteChange]);

  const isShellLocked = useCallback(
    (shell: (typeof accountShells)[number]) =>
      !!shell.fundingSourceId && partnerSharedFundingSourceIds.has(shell.fundingSourceId),
    [partnerSharedFundingSourceIds],
  );

  const regularShells = useMemo(
    () => accountShells.filter(s => !isShellLocked(s)),
    [accountShells, isShellLocked],
  );
  const lockedShells = useMemo(
    () => accountShells.filter(s => !s.isExistingLtcPolicy && isShellLocked(s)),
    [accountShells, isShellLocked],
  );

  if (isLoadingPartnerData) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="text-primary h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className={cn('space-y-4', className)}>
        {regularShells.map(shell =>
          shell.isExistingLtcPolicy ? (
            <ExistingLtcPolicyCard
              key="existing-ltc-policy"
              label={shell.label}
              hasPolicy={Boolean(shell.policyFundingSourceId)}
              policyFaceAmount={shell.policyMaximumBenefitAmount ?? undefined}
              policyTypeName={shell.policyTypeName ?? undefined}
              onClick={() => setExistingLtcDrawerOpen(true)}
            />
          ) : (
            <AccountCard
              key={getShellExpandKey(shell)}
              assetTypeId={shell.assetTypeId}
              label={shell.label}
              isExpanded={expandedShellKey === getShellExpandKey(shell)}
              onToggle={() => handleToggle(getShellExpandKey(shell))}
              fundingSourceId={shell.fundingSourceId}
              existingSource={shell.selfFundingSource}
              onSaveComplete={() => handleSaveComplete(getShellExpandKey(shell))}
              disabled={readOnly}
              beforeSave={beforeAccountSave}
              allocationAmount={
                shell.fundingSourceId ? allocationsBySourceId[shell.fundingSourceId] : undefined
              }
              allocation={buildAllocationCallbacks(
                shell.fundingSourceId ?? null,
                shell.assetTypeId,
                shell.selfFundingSource,
              )}
            />
          ),
        )}

        {lockedShells.length > 0 && (
          <>
            <div className="pt-6">
              <h3 className="text-base font-semibold text-gray-950">
                Shared by {partnerFirstName ?? 'Partner'}
              </h3>
              <p className="text-foreground mt-1 text-sm">
                These accounts were shared from {partnerFirstName ?? 'your partner'}&apos;s plan and
                are included in your coverage assessment.
              </p>
            </div>
            {lockedShells.map(shell => (
              <AccountCard
                key={getShellExpandKey(shell)}
                assetTypeId={shell.assetTypeId}
                label={shell.label}
                isExpanded={expandedShellKey === getShellExpandKey(shell)}
                onToggle={() => handleToggle(getShellExpandKey(shell))}
                fundingSourceId={shell.fundingSourceId}
                existingSource={shell.selfFundingSource}
                onSaveComplete={() => handleSaveComplete(getShellExpandKey(shell))}
                disabled={readOnly}
                lockedByPartnerName={partnerFirstName ?? undefined}
              />
            ))}
          </>
        )}
      </div>
      <AddPolicyModal
        open={existingLtcDrawerOpen}
        onClose={() => setExistingLtcDrawerOpen(false)}
        fundingSourceId={policyFundingSourceIdForDrawer}
        readOnly={readOnly || !!beforeAccountSave}
      />
    </>
  );
}
